import { useUtils } from "@/app/hooks/useUtils";
import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { Fragment, useRef, useState } from "react";

export interface IRotaFiltersFormFields {
  name?: string;
  cancelreason?: string;
  cancel_reason_name?: string;
}
export interface ActionBodyForProps {
  sub_order_id?: string;
  internal_order_status?: string;
  cancel_reason?: any;
}

const STATUS_LABEL_MAP: Record<string, string> = {
  payment_captured: "Payment Captured",
  rx_pending: "Rx Pending",
  pending: "Pending",
  payment_due: "Payment Due",
  confirmed: "Confirmed",
  ready_for_pickup: "Ready for Pickup",
  packed: "Packed",
  picked: "Picked",
  delivered: "Delivered",
  cancelled: "Cancelled",
  undelivered: "Undelivered",
  returned: "Returned",
  return_initiated: "Return Initiated",
  return_in_transit: "Reverse In Transit",
  undelivered_in_transit: "Reverse In Transit",
};

const STATUS_STYLE_MAP: Record<string, { dot: string; badge: string }> = {
  payment_captured: { dot: "border-[#16A34A]", badge: "bg-[#ECFDF3] text-[#067647] ring-[#ABEFC6]" },
  rx_pending: { dot: "border-[#9CA3AF]", badge: "bg-[#F3F4F6] text-[#374151] ring-[#D1D5DB]" },
  payment_due: { dot: "border-[#F59E0B]", badge: "bg-[#FFFBEB] text-[#B45309] ring-[#FCD34D]" },
  pending: { dot: "border-[#F59E0B]", badge: "bg-[#FFFBEB] text-[#B45309] ring-[#FCD34D]" },
  confirmed: { dot: "border-[#3B82F6]", badge: "bg-[#EFF6FF] text-[#1D4ED8] ring-[#BFDBFE]" },
  ready_for_pickup: { dot: "border-[#6366F1]", badge: "bg-[#EEF2FF] text-[#4338CA] ring-[#C7D2FE]" },
  packed: { dot: "border-[#2563EB]", badge: "bg-[#DBEAFE] text-[#1D4ED8] ring-[#93C5FD]" },
  picked: { dot: "border-[#0284C7]", badge: "bg-[#E0F2FE] text-[#0369A1] ring-[#7DD3FC]" },
  delivered: { dot: "border-[#16A34A]", badge: "bg-[#ECFDF3] text-[#067647] ring-[#ABEFC6]" },
  cancelled: { dot: "border-[#DC2626]", badge: "bg-[#FEF2F2] text-[#B91C1C] ring-[#FCA5A5]" },
  undelivered: { dot: "border-[#DC2626]", badge: "bg-[#FEF2F2] text-[#B91C1C] ring-[#FCA5A5]" },
  return_initiated: { dot: "border-[#EA580C]", badge: "bg-[#FFF7ED] text-[#C2410C] ring-[#FDBA74]" },
  return_in_transit: { dot: "border-[#EA580C]", badge: "bg-[#FFF7ED] text-[#C2410C] ring-[#FDBA74]" },
  undelivered_in_transit: { dot: "border-[#EA580C]", badge: "bg-[#FFF7ED] text-[#C2410C] ring-[#FDBA74]" },
  returned: { dot: "border-[#7C3AED]", badge: "bg-[#F5F3FF] text-[#6D28D9] ring-[#C4B5FD]" },
};

const formatStatusLabel = (value: string) =>
  value
    ?.split("_")
    ?.map((word) => word?.charAt(0)?.toUpperCase() + word?.slice(1))
    ?.join(" ");

export default function OrderTrailsComponent({ ordertrails, setOrdertrails }) {
  const cancelButtonRef = useRef(null);
  const [open, setOpen] = useState(true);
  const { veticDateFormat } = useUtils();

  const getPrimaryDetailText = (step) => {
    if (step?.payment_method) return `Payment Method ${step.payment_method}`;
    if (step?.cancelled_by) return `Cancelled by ${step.cancelled_by}`;
    if (step?.packed_by) return `Packed by ${step.packed_by}`;
    if (step?.created_by) return `Created by ${step.created_by}`;
    if (step?.updated_by) return `Updated by ${step.updated_by}`;
    if (step?.generated_by) return `Generated by ${step.generated_by}`;
    if (step?.returned_accepted_by) return `Return by ${step.returned_accepted_by}`;
    if (step?.rider_name) return `Rider Name ${step.rider_name}`;
    return "";
  };

  const closeModal = () => {
    setOrdertrails(null);
    setOpen(false);
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-40" initialFocus={cancelButtonRef} onClose={closeModal}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-[#101828]/45 backdrop-blur-[1px] transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative w-full transform overflow-hidden rounded-2xl border border-[#EAECF0] bg-white text-left shadow-xl transition-all sm:my-4 sm:max-w-3xl">
                <div className="flex items-center justify-between border-b border-[#EAECF0] px-4 py-3 sm:px-6">
                  <div>
                    <Dialog.Title className="text-base font-semibold text-[#101828]">Order Status History</Dialog.Title>
                    <p className="mt-0.5 text-xs text-[#667085]">{ordertrails?.length || 0} updates</p>
                  </div>
                  <button
                    type="button"
                    className="rounded-md p-1 text-gray-500 transition-colors hover:bg-[#F2F4F7] hover:text-[#101828]"
                    onClick={closeModal}
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </div>

                <div className="max-h-[65vh] overflow-y-auto px-4 py-4 sm:px-6">
                  {ordertrails?.length ? (
                    <ol className="space-y-3">
                      {ordertrails?.map((step, idx) => {
                        const statusStyle = STATUS_STYLE_MAP[step?.status] || {
                          dot: "border-[#9CA3AF]",
                          badge: "bg-[#F3F4F6] text-[#374151] ring-[#D1D5DB]",
                        };
                        const detailText = getPrimaryDetailText(step);

                        return (
                          <li key={idx} className="relative pl-7">
                            {idx < ordertrails.length - 1 && (
                              <span className="absolute left-[6px] top-5 h-[calc(100%+0.85rem)] w-px bg-[#E4E7EC]" />
                            )}
                            <span
                              className={`absolute left-0 top-[7px] h-3.5 w-3.5 rounded-full border-2 bg-white ${statusStyle.dot}`}
                            />

                            <div className="rounded-xl border border-[#EAECF0] bg-[#FCFCFD] p-3">
                              <span
                                className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold ring-1 ring-inset ${statusStyle.badge}`}
                              >
                                {STATUS_LABEL_MAP[step?.status] || formatStatusLabel(step?.status)}
                              </span>
                              <p className="mt-2 text-[10px] text-[#667085]">
                                {veticDateFormat(step?.created_at)}
                                {detailText ? `    • ${detailText}` : ""}
                              </p>
                            </div>
                          </li>
                        );
                      })}
                    </ol>
                  ) : (
                    <div className="rounded-xl border border-dashed border-[#D0D5DD] bg-[#F9FAFB] px-4 py-8 text-center text-sm text-[#667085]">
                      No status history available
                    </div>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
